import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NotificationHub from '../components/NotificationHub';
import { API_BASE_URL } from '../config/api';
import './css/UserHomepage.css';

function UserHomepage() {
  const [userData, setUserData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    bio: '',
    location: '',
    gender: 'N',
    profile_picture: null
  });
  const [editingField, setEditingField] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [profilePicture, setProfilePicture] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in before fetching profile
    const storedUsername = localStorage.getItem('username');
    if (storedUsername) {
      fetchUserProfile();
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchUserProfile = async () => {
    // Check if user is logged in
    const storedUsername = localStorage.getItem('username');
    if (!storedUsername) {
      navigate('/login');
      return;
    }
    
    try {
      // Get the CSRF token from cookies
      const csrftoken = document.cookie.split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];
      
      const response = await fetch(`${API_BASE_URL}/api/user-profile/`, {
        method: 'GET',
        credentials: 'include', // Include cookies in the request
        headers: {
          'X-CSRFToken': csrftoken || '',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserData(data);
      } else if (response.status === 401) {
        // If unauthorized, redirect to login
        navigate('/login');
      } else {
        // Handle other errors
        setNotifications((prev) => [
          ...prev,
          { id: Date.now(), msgText: '获取用户信息失败，请稍后再试。' },
        ]);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), msgText: '获取用户信息失败，请稍后再试。' },
      ]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value
    });
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current.click();
  };

  const handleProfilePictureChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfilePicture(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setUserData({
          ...userData,
          profile_picture: event.target.result
        });
      };
      reader.readAsDataURL(file);
      
      // Upload immediately
      uploadProfilePicture(file);
    }
  };

  const uploadProfilePicture = async (file) => {
    const formData = new FormData();
    formData.append('profile_picture', file);
    
    try {
      // Get the CSRF token from cookies
      const csrftoken = document.cookie.split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];
      
      const response = await fetch(`${API_BASE_URL}/api/user-profile/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRFToken': csrftoken || '',
        },
        body: formData,
      });
      
      if (response.ok) {
        setNotifications((prev) => [
          ...prev,
          { id: Date.now(), msgText: '头像已成功更新。' },
        ]);
      } else {
        setNotifications((prev) => [
          ...prev,
          { id: Date.now(), msgText: '更新头像失败，请稍后再试。' },
        ]);
      }
    } catch (error) {
      console.error('Error updating profile picture:', error);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), msgText: '更新头像时发生错误，请稍后再试。' },
      ]);
    }
  };

  const startEditing = (fieldName) => {
    setEditingField(fieldName);
  };

  const saveField = async (fieldName) => {
    if (editingField !== fieldName) return;
    
    setEditingField(null);
    
    const formData = new FormData();
    formData.append(fieldName, userData[fieldName]);
    
    try {
      // Get the CSRF token from cookies
      const csrftoken = document.cookie.split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];
      
      const response = await fetch(`${API_BASE_URL}/api/user-profile/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRFToken': csrftoken || '',
        },
        body: formData,
      });
      
      if (response.ok) {
        setNotifications((prev) => [
          ...prev,
          { id: Date.now(), msgText: '个人资料已成功更新。' },
        ]);
      } else {
        setNotifications((prev) => [
          ...prev,
          { id: Date.now(), msgText: '更新个人资料失败，请稍后再试。' },
        ]);
        // Revert to original data on error
        fetchUserProfile();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), msgText: '更新个人资料时发生错误，请稍后再试。' },
      ]);
      // Revert to original data on error
      fetchUserProfile();
    }
  };

  const handleNotificationDismiss = (id) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  };

  const handleGenderChange = (gender) => {
    setUserData({
      ...userData,
      gender
    });
    
    // Save immediately
    const formData = new FormData();
    formData.append('gender', gender);
    
    try {
      // Get the CSRF token from cookies
      const csrftoken = document.cookie.split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];
      
      fetch(`${API_BASE_URL}/api/user-profile/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRFToken': csrftoken || '',
        },
        body: formData,
      })
      .then(response => {
        if (response.ok) {
          setNotifications((prev) => [
            ...prev,
            { id: Date.now(), msgText: '性别已成功更新。' },
          ]);
        } else {
          setNotifications((prev) => [
            ...prev,
            { id: Date.now(), msgText: '更新性别失败，请稍后再试。' },
          ]);
          // Revert to original data on error
          fetchUserProfile();
        }
      });
    } catch (error) {
      console.error('Error updating gender:', error);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), msgText: '更新性别时发生错误，请稍后再试。' },
      ]);
      // Revert to original data on error
      fetchUserProfile();
    }
  };

  const renderEditableField = (fieldName, label, type = 'text', options = null) => {
    const isEditing = editingField === fieldName;
    const value = userData[fieldName] || '';
    
    // Special case for gender field
    if (fieldName === 'gender') {
      return (
        <div className="profile-section gender-section">
          <h3>{label}</h3>
          <div className="gender-buttons">
            <button 
              className={`gender-btn ${userData.gender === 'M' ? 'active' : ''}`}
              onClick={() => handleGenderChange('M')}
            >
              男
            </button>
            <button 
              className={`gender-btn ${userData.gender === 'F' ? 'active' : ''}`}
              onClick={() => handleGenderChange('F')}
            >
              女
            </button>
            <button 
              className={`gender-btn ${userData.gender === 'N' ? 'active' : ''}`}
              onClick={() => handleGenderChange('N')}
            >
              未设置
            </button>
          </div>
        </div>
      );
    }
    
    if (isEditing) {
      if (type === 'textarea') {
        return (
          <div className="profile-section editable">
            <h3>{label}</h3>
            <textarea
              name={fieldName}
              value={value}
              onChange={handleInputChange}
              onBlur={() => saveField(fieldName)}
              rows="4"
              autoFocus
            />
          </div>
        );
      } else {
        return (
          <div className="profile-section editable">
            <h3>{label}</h3>
            <input
              type={type}
              name={fieldName}
              value={value}
              onChange={handleInputChange}
              onBlur={() => saveField(fieldName)}
              autoFocus
            />
          </div>
        );
      }
    } else {
      // For gender field, show the translated value
      let displayValue = value;
      if (fieldName === 'gender') {
        displayValue = value === 'M' ? '男' : value === 'F' ? '女' : '未设置';
      }
      
      // Only show non-empty fields or always show certain fields
      if (value || fieldName === 'gender') {
        return (
          <div 
            className="profile-section" 
            onClick={() => startEditing(fieldName)}
          >
            <h3>{label}</h3>
            <p>{displayValue}</p>
          </div>
        );
      }
      return (
        <div 
          className="profile-section empty" 
          onClick={() => startEditing(fieldName)}
        >
          <h3>{label}</h3>
          <p className="placeholder">点击添加{label}</p>
        </div>
      );
    }
  };

  return (
    <div className="user-homepage">
      <div className="user-title">
        <h1>我的主页</h1>
      </div>
      <div className="user-content-wrapper">
        <div className="user-content">
          <div className="profile-view">
            <div className="profile-header">
              <div 
                className="profile-picture-container"
                onClick={handleProfilePictureClick}
              >
                {userData.profile_picture ? (
                  <img 
                    src={userData.profile_picture} 
                    alt={userData.username} 
                    className="profile-picture" 
                  />
                ) : (
                  <div className="profile-picture-placeholder">
                    {userData.username ? userData.username.charAt(0).toUpperCase() : '?'}
                  </div>
                )}
                <div className={`profile-picture-overlay ${!userData.profile_picture ? 'always-visible' : ''}`}>
                  <span>更换头像</span>
                </div>
                <input 
                  type="file" 
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept="image/*" 
                  onChange={handleProfilePictureChange} 
                />
              </div>
              <div className="profile-info">
                <h2>{userData.username}</h2>
                {editingField === 'location' ? (
                  <input
                    type="text"
                    name="location"
                    placeholder="所在地"
                    value={userData.location || ''}
                    onChange={handleInputChange}
                    onBlur={() => saveField('location')}
                    autoFocus
                  />
                ) : (
                  <p 
                    className={`location ${!userData.location ? 'empty' : ''}`}
                    onClick={() => startEditing('location')}
                  >
                    {userData.location || '点击添加所在地'}
                  </p>
                )}
              </div>
            </div>
            
            <div className="profile-details">
              {renderEditableField('bio', '个人简介', 'textarea')}
              {renderEditableField('email', '电子邮箱', 'email')}
              {renderEditableField('gender', '性别', 'select', [
                { value: 'N', label: '未设置' },
                { value: 'M', label: '男' },
                { value: 'F', label: '女' }
              ])}
            </div>
          </div>
        </div>
      </div>
      <NotificationHub 
        duration={1500} 
        notifications={notifications} 
        onNotificationDismiss={handleNotificationDismiss} 
      />
    </div>
  );
}

export default UserHomepage;
